import { useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", enquiry_type: "general", subject: "", order_id: "", preferred_contact: "email", message: "" });
  const [loading, setLoading] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTicketNumber("");
    try {
      const response = await api.post("/contact", form);
      setTicketNumber(response.data.ticket_number || "");
      toast.success("Your enquiry has been logged.");
      setForm({ name: "", email: "", phone: "", enquiry_type: "general", subject: "", order_id: "", preferred_contact: "email", message: "" });
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-5 pt-[132px] sm:px-8 md:pb-6 md:pt-[136px]">
      <SEO title="Contact Us" description="Contact Nityavastra — Bhubaneswar, Kalyani Nagar Rd. WhatsApp +91 8281037035, email hello@nityavastra.com. We reply within 24 hours." />
      <div className="mb-7">
        <p className="uppercase text-xs tracking-[0.3em] text-[#7C1F30] mb-3">Get in touch</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1C1917] leading-tight max-w-2xl">
          We'd love to hear from you.
        </h1>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="space-y-3 md:col-span-1">
          {[
            { icon: MapPin, title: "Visit", body: "Bhubaneswar, Kalyani Nagar Rd\n751019" },
            { icon: Phone, title: "Call", body: "+91 8281037035\nMon–Sat, 10am–7pm" },
            { icon: Mail, title: "Write", body: "hello@nityavastra.com" },
          ].map((c) => (
            <div key={c.title} className="rounded-lg border border-[#E7E5E4] bg-white p-4">
              <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-[#F5F5F4]">
                <c.icon className="h-5 w-5 text-[#7C1F30]" />
              </div>
              <h3 className="font-serif text-lg mb-2">{c.title}</h3>
              <p className="text-sm text-[#1C1917]/75 whitespace-pre-line">{c.body}</p>
            </div>
          ))}
        </div>

        <div className="md:col-span-2">
          <form onSubmit={submit} className="space-y-4 rounded-lg border border-[#E7E5E4] bg-white p-5 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                  className="bg-white"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                  className="bg-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Mobile Number</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91"
                  data-testid="contact-phone"
                  className="bg-white"
                />
              </div>
              <div>
                <Label>Enquiry Type</Label>
                <select
                  required
                  value={form.enquiry_type}
                  onChange={(e) => setForm({ ...form, enquiry_type: e.target.value })}
                  data-testid="contact-enquiry-type"
                  className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                >
                  <option value="general">General Enquiry</option>
                  <option value="product">Product Information</option>
                  <option value="order">Order Assistance</option>
                  <option value="payment">Payment Issue</option>
                  <option value="shipping">Shipping or Delivery</option>
                  <option value="return-refund">Return or Refund</option>
                  <option value="seller">Partner Enquiry</option>
                  <option value="partnership">Business Partnership</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Subject</Label>
              <Input
                required
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Briefly describe your enquiry"
                data-testid="contact-subject"
                className="bg-white"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Order ID <span className="text-[#78716C]">(if applicable)</span></Label>
                <Input
                  value={form.order_id}
                  onChange={(e) => setForm({ ...form, order_id: e.target.value })}
                  placeholder="Example: NV-123456"
                  data-testid="contact-order-id"
                  className="bg-white"
                />
              </div>
              <div>
                <Label>Preferred Contact Method</Label>
                <select
                  required
                  value={form.preferred_contact}
                  onChange={(e) => setForm({ ...form, preferred_contact: e.target.value })}
                  data-testid="contact-preferred-contact"
                  className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone Call</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Message</Label>
              <Textarea
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
                className="bg-white"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="bg-[#7C1F30] hover:bg-[#8D2A3D] text-white rounded-full py-6 px-10 uppercase text-xs tracking-[0.2em]"
            >
              {loading ? "Sending..." : "Send Message"}
            </Button>
            {ticketNumber && (
              <p role="status" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                Your enquiry has been logged. Please keep your ticket number: <strong>{ticketNumber}</strong>.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
